import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel, InputAdornment, TextField } from '@mui/material';
import { Search as SearchIcon, CreateNewFolderOutlined as CreateNewFolderIcon } from '@mui/icons-material';
import PostAddIcon from '@mui/icons-material/PostAdd';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ToastContainer, toast } from 'react-toastify';
import PuffLoader from 'react-spinners/PuffLoader';
import { PDFDocument } from 'pdf-lib';
import DocumentService from '../../Services/documents.service.js';
import UserService from '../../Services/user.service.js';
import SubAccountantService from '../../Services/subAccountant.service.js';
import EnumsService from '../../Services/enums.service.js';
import ClientDocumentCountService from '../../Services/ClientDocumentCount.service.js';
import { useSignalR } from '../../Contexts/signalR.js';
import { PermissionsContext } from '../../Contexts/Permissions.js';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext.js';
import Constants from '../../assests/Constants/constants.js';
import NoDocumentsPdf from '../../assests/pdf/NoDocument.pdf';
import DocumentTransferDialog from '../../Components/DocumentTransferDialog/DocumentTransferDialog.js';
import DraggableModal from '../../Components/DraggableModal/DraggableModal.js';
import NavigationButton from '../../Components/NavigationButton/NavigationButton.jsx';
import FilterMonthYearPicker from '../../Components/Widgets/CustomDates/FilterMonthYearPicker/FilterMonthYearPicker.jsx';
import XtramModal, { XtramButton, XtramNotice, XtramSelect } from '../../Components/Widgets/XtramModal/XtramModal.jsx';
import OtherDocumentDataGrid from '../../Components/OtherDocuments/SubDocuments/OtherDocumentDataGrid.jsx';
import OtherDocumentCreateModal from '../../Components/OtherDocuments/SubDocuments/OtherDocumentCreateModal.jsx';
import OtherDocumentDownloadModal from '../../Components/OtherDocuments/SubDocuments/OtherDocumentDownloadModal.jsx';
import OtherDocumentEditModal from '../../Components/OtherDocuments/SubDocuments/OtherDocumentEditModal.jsx';
import OtherDocumentCategoriesModal from '../../Components/OtherDocuments/SubDocuments/OtherDocumentCategoriesModal.jsx';
import OtherDocumentTypesModal from '../../Components/OtherDocuments/SubDocuments/OtherDocumentTypesModal.jsx';
import { calendarDateToIso, formatDayMonthYear } from '../../Components/OtherDocuments/SubDocuments/otherDocumentDates';
import { getTypesForSection, isPermanentFileName } from '../../Utils/otherDocumentSections';
import headerStyles from '../../Components/ClientDetails/SubComponents/ClientDetailsHeader/HeaderToolbar.module.scss';
import pageStyles from './OtherDocument.module.scss';
import './OtherDocument.css';

const normalize = (value) => (value || '').trim().toLowerCase();

const toFiles = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 100);
};

// Mid-month UTC dates, as FilterMonthYearPicker expects.
const monthOf = (year, month) => new Date(Date.UTC(year, month, 10));

function OtherDocument() {
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n, t } = useTranslation();
    const direction = i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr';
    const type = location.state?.type || 'OtherDocuments';
    const dataGridRef = useRef();
    const fileInputRef = useRef();
    const { documentUpdate } = useSignalR();
    const { setMenuProps } = useHamburgerMenu();
    const { userTypeContext, permissionsState, clientPermissionsState } = useContext(PermissionsContext);

    const [user, setUser] = useState(null);
    const [token, setToken] = useState('');
    const [client, setClient] = useState(null);
    const [types, setTypes] = useState([]);
    const [titles, setTitles] = useState([]);
    const [enumsLoaded, setEnumsLoaded] = useState(false);
    const [section, setSection] = useState(() => ({
        titleId: location.state?.titleId || null,
        isPermanent: !!location.state?.isPermenantCase || isPermanentFileName(location.state?.subItem),
    }));
    const [data, setData] = useState([]);
    const [subAccountantOptions, setSubAccountantOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [selectedType, setSelectedType] = useState(location.state?.subItem || '');
    const [searchQuery, setSearchQuery] = useState('');
    const [onlyUnprocessed, setOnlyUnprocessed] = useState(false);
    const [filterByDateRange, setFilterByDateRange] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState(() => monthOf(new Date().getFullYear(), 0));
    const [filterEndDate, setFilterEndDate] = useState(() => monthOf(new Date().getFullYear(), new Date().getMonth()));
    const [selectedRows, setSelectedRows] = useState([]);

    const [createOpen, setCreateOpen] = useState(false);
    const [downloadOpen, setDownloadOpen] = useState(false);
    const [statusOptionsOpen, setStatusOptionsOpen] = useState(false);
    const [categoriesOpen, setCategoriesOpen] = useState(false);
    // null, 'list', or 'add' to open straight into the new-type form.
    const [typesModal, setTypesModal] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [fileTransported, setFileTransported] = useState(null);
    const [multiTransferItems, setMultiTransferItems] = useState(null);
    const [pendingDeletes, setPendingDeletes] = useState([]);
    const [openedIframeUrl, setOpenedIframeUrl] = useState('');
    const [bigIframeModalOpen, setBigIframeModalOpen] = useState(false);

    const isClient = userTypeContext?.toString() === Constants.UserType.Client;
    const isAccountantOrSubAccountant = [Constants.UserType.Accountant, Constants.UserType.SubAccountant]
        .includes(userTypeContext?.toString());
    const isClientWithoutPremium = isClient && !permissionsState?.Premium;
    const hideStatusBadge = !!clientPermissionsState?.CantSeeStatusDocuments && isClient;
    const canChangeStatus = !hideStatusBadge && !isClientWithoutPremium;

    useEffect(() => {
        setMenuProps({ client });
    }, [client, setMenuProps]);

    // ─── Loading ────────────────────────────────
    const refreshClientCounts = useCallback(async (baseClient, currentToken) => {
        if (!baseClient?.caseId) return;
        try {
            const counts = await ClientDocumentCountService.getClientDocumentCountByCaseId(baseClient.caseId, currentToken);
            setClient((prev) => ({
                ...(prev?.caseId === baseClient.caseId ? prev : baseClient),
                totalCounts: counts?.totalCounts || 0,
                documentCounts: counts?.documentCounts || {},
                documentIds: counts?.documentIds || {},
            }));
        } catch (error) {
            console.error('Error loading document counts:', error);
            setClient((prev) => (prev?.caseId === baseClient.caseId ? prev : baseClient));
        }
    }, []);

    const loadEnums = useCallback(async (currentUser, currentToken) => {
        if (!currentUser?.accountantId || !currentToken) return;
        try {
            const [typesResponse, titlesResponse] = await Promise.all([
                EnumsService.GetOtherDocumentsTypesByAccountantId(currentUser.accountantId, currentToken),
                EnumsService.GetOtherDocumentsTitlesByAccountantId(currentUser.accountantId, currentToken).catch(() => null),
            ]);
            const typesData = typesResponse.ok ? await typesResponse.json() : [];
            const titlesData = titlesResponse?.ok ? await titlesResponse.json() : [];
            setTypes(Array.isArray(typesData) ? typesData : []);
            setTitles(Array.isArray(titlesData) ? titlesData : []);
            if (!typesResponse.ok) toast.error(t('fetchEnumsError'));
        } catch (error) {
            console.error(error);
            toast.error(t('fetchEnumsError'));
        } finally {
            setEnumsLoaded(true);
        }
    }, [t]);

    const loadSubAccountants = useCallback(async (currentUser, currentToken, currentClient) => {
        try {
            const [subAccountants, accountant] = await Promise.all([
                SubAccountantService.GetSubAccountantByAccountantId(currentToken, currentUser.accountantId),
                SubAccountantService.GetAccountantById(currentToken, currentUser.accountantId),
            ]);
            const options = [
                { value: accountant?.accountantId, label: accountant?.name },
                ...(Array.isArray(subAccountants) ? subAccountants : []).map((sub) => ({ value: sub.subAccountantId, label: sub.name })),
            ];
            if (currentClient) options.push({ value: currentClient.caseId, label: currentClient.name });
            setSubAccountantOptions(options.filter((option) => option.value));
        } catch (error) {
            console.error(error);
            toast.error(t('fetchSubAccountantsDataError'));
        }
    }, [t]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setEnumsLoaded(false);
            try {
                const currentUser = await UserService.getCurrentUser();
                const currentToken = await UserService.getCurrentToken();
                if (!currentUser || !currentToken) {
                    navigate('/login');
                    return;
                }
                setUser(currentUser);
                setToken(currentToken);
                const stateClient = location.state?.client;
                await Promise.all([
                    stateClient ? refreshClientCounts(stateClient, currentToken) : null,
                    loadEnums(currentUser, currentToken),
                    loadSubAccountants(currentUser, currentToken, stateClient),
                ]);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.client]);

    useEffect(() => {
        Object.keys(documentUpdate || {}).forEach((updateCaseId) => {
            const update = documentUpdate[updateCaseId];
            setClient((prev) => (prev?.caseId === updateCaseId
                ? { ...prev, totalCounts: update.totalCounts, documentIds: update.documentsIds, documentsCount: update.documentsCount }
                : prev));
        });
    }, [documentUpdate]);

    const activeTitle = useMemo(
        () => (section.titleId ? titles.find((title) => title.id === section.titleId) || null : null),
        [section.titleId, titles]
    );
    const isPermanentSection = !activeTitle && section.isPermanent;
    const sectionTypes = useMemo(
        () => getTypesForSection(types, titles, { titleId: activeTitle?.id || null, isPermanent: isPermanentSection }),
        [types, titles, activeTitle, isPermanentSection]
    );
    const sectionTypeNames = useMemo(() => sectionTypes.map((item) => item.name).filter(Boolean), [sectionTypes]);

    const loadDocuments = useCallback(async () => {
        if (!client?.caseId || !token) return;
        if (sectionTypeNames.length === 0) {
            setData([]);
            return;
        }
        setLoading(true);
        try {
            const response = await DocumentService.GetOtherDocumentsByTypes(client.caseId, sectionTypeNames, token);
            if (response.ok) {
                const documents = await response.json();
                setData(Array.isArray(documents) ? documents : []);
            } else {
                toast.error(`${t('fetchDocumentsError')}: ${await response.text()}`);
            }
        } catch (error) {
            console.error(error);
            toast.error(`${t('fetchDocumentsError')}: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [client?.caseId, token, sectionTypeNames, t]);

    useEffect(() => {
        if (enumsLoaded) loadDocuments();
    }, [enumsLoaded, loadDocuments]);

    const reloadAll = useCallback(() => loadEnums(user, token), [loadEnums, user, token]);

    // ─── Transfers into this page ───────────────
    useEffect(() => {
        if (!client || !location.state) return;
        const state = location.state;
        if (state.isMultiTransfer && Array.isArray(state.transferItems) && state.transferItems.length > 0) {
            if (sessionStorage.getItem('hasMultiTransferredOtherDocuments')) return;
            setFileTransported(state.transferItems.flatMap((item) => toFiles(item.files)));
            setMultiTransferItems(state.transferItems);
            setPendingDeletes([]);
            setCreateOpen(true);
            sessionStorage.setItem('hasMultiTransferredOtherDocuments', 'true');
            return;
        }
        if (state.fileTransported && state.dto && !sessionStorage.getItem('hasTransferredOtherDocuments')) {
            setFileTransported(state.fileTransported);
            setMultiTransferItems(null);
            setPendingDeletes(state.idToDelete && state.fileTypeToDelete ? [{ id: state.idToDelete, fromType: state.fileTypeToDelete }] : []);
            setCreateOpen(true);
            sessionStorage.setItem('hasTransferredOtherDocuments', 'true');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client?.caseId, location.state]);

    // ─── Derived data ───────────────────────────
    const unprocessedIds = useMemo(() => new Set(client?.documentIds?.otherDocuments || []), [client]);

    const titleNameByType = useMemo(() => {
        const names = new Map(titles.map((title) => [title.id, title.name]));
        const result = {};
        types.forEach((item) => {
            if (item?.titleId && names.has(item.titleId)) result[item.name] = names.get(item.titleId);
        });
        return result;
    }, [titles, types]);

    const filteredData = useMemo(() => {
        const query = normalize(searchQuery);
        const typeKey = normalize(selectedType);
        const rangeStart = new Date(filterStartDate.getFullYear(), filterStartDate.getMonth(), 1);
        const rangeEnd = new Date(filterEndDate.getFullYear(), filterEndDate.getMonth() + 1, 1);
        return data.filter((item) => {
            if (typeKey && normalize(item.documentType) !== typeKey) return false;
            if (onlyUnprocessed && !unprocessedIds.has(item.id)) return false;
            if (query) {
                const date = formatDayMonthYear(item.documentDate);
                const haystack = [item.title, item.documentType, date, date.replace(/\//g, '-'), item.uploadNumber?.toString()];
                if (!haystack.some((value) => value && value.toLowerCase().includes(query))) return false;
            }
            if (filterByDateRange) {
                const docDate = item.documentDate ? new Date(item.documentDate) : null;
                if (!docDate || Number.isNaN(docDate.getTime()) || docDate < rangeStart || docDate >= rangeEnd) return false;
            }
            return true;
        }).sort((a, b) => (Number(b.uploadNumber) || 0) - (Number(a.uploadNumber) || 0));
    }, [data, searchQuery, selectedType, onlyUnprocessed, unprocessedIds, filterByDateRange, filterStartDate, filterEndDate]);

    const typeFilterOptions = useMemo(() => {
        const options = [{ value: '', label: t('All') }, ...sectionTypes.map((item) => ({ value: item.name, label: item.name }))];
        if (selectedType && !options.some((option) => normalize(option.value) === normalize(selectedType))) {
            options.push({ value: selectedType, label: selectedType });
        }
        return options;
    }, [sectionTypes, selectedType, t]);

    const editingItem = useMemo(() => data.find((item) => item.id === editingId) || null, [data, editingId]);
    const editingIndex = editingItem ? filteredData.findIndex((item) => item.id === editingId) : -1;

    useEffect(() => {
        if (editingId && !loading && !editingItem) setEditingId(null);
    }, [editingId, editingItem, loading]);

    // ─── Status ─────────────────────────────────
    const setProcessed = async (ids, processed) => {
        const list = ids.filter(Boolean);
        if (!list.length || !client) return true;
        const single = list.length === 1;
        const errorKey = single ? 'DocumentNotProcessed' : 'DocumentsNotProcessed';
        try {
            const { caseId, accountantId } = client;
            const response = single
                ? await (processed
                    ? ClientDocumentCountService.removeDocumentByTypeAndId(caseId, accountantId, type, list[0], token)
                    : ClientDocumentCountService.addDocumentByTypeAndId(caseId, accountantId, type, list[0], token))
                : await (processed
                    ? ClientDocumentCountService.removeDocumentsByTypeAndIds(caseId, accountantId, type, list, token)
                    : ClientDocumentCountService.addDocumentsByTypeAndIds(caseId, accountantId, type, list, token));
            if (response.ok) {
                await refreshClientCounts(client, token);
                toast.success(t('DocumentProcessed'));
                return true;
            }
            toast.error(t(errorKey, { errorText: await response.text() }));
        } catch (error) {
            toast.error(t(errorKey, { errorText: error.message }));
        }
        return false;
    };

    const handleRightClickAction = async (id, status) => {
        if (!canChangeStatus) return;
        setLoading(true);
        await setProcessed([id], status === 'done');
        setLoading(false);
    };

    const applyBulkStatus = async (processed) => {
        setSaving(true);
        await setProcessed(selectedRows, processed);
        setSaving(false);
        dataGridRef.current?.deselectRows(selectedRows);
        setSelectedRows([]);
        setStatusOptionsOpen(false);
    };

    // ─── Edit / delete ──────────────────────────
    const handleUpdate = async ({ title, documentType, documentDate, uploadDate, uploadNumber, processedBy, processed }) => {
        if (!editingItem) return false;
        setSaving(true);
        try {
            const response = await DocumentService.UpdateDocument(type, token, {
                id: editingItem.id,
                caseId: editingItem.caseId,
                title,
                documentType,
                documentDate: calendarDateToIso(documentDate),
                ...(uploadDate ? { uploadDate: calendarDateToIso(uploadDate) } : {}),
                ...(uploadNumber !== undefined ? { uploadNumber } : {}),
                ...(processedBy !== undefined ? { processedBy } : {}),
            });
            if (!response.ok) {
                toast.error(`${t('documentUpdateError')}: ${await response.text()}`);
                return false;
            }
            if (processed !== undefined) await setProcessed([editingItem.id], processed);
            toast.success(t('documentUpdateSuccess'));
            await loadDocuments();
            return true;
        } catch (error) {
            console.error(error);
            toast.error(t('documentUpdateError'));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        const target = deleteTarget;
        if (!target) return;
        setSaving(true);
        try {
            const response = await DocumentService.DeleteDocument(type, target.id, token);
            if (!response.ok) {
                toast.error(`${t('documentDeleteError')}: ${await response.text()}`);
                return;
            }
            toast.success(t('documentDeleteSuccess'));
            if (editingId === target.id) {
                const index = filteredData.findIndex((item) => item.id === target.id);
                const next = filteredData[index + 1] || filteredData[index - 1];
                setEditingId(next ? next.id : null);
            }
            setDeleteTarget(null);
            await Promise.all([loadDocuments(), refreshClientCounts(client, token)]);
        } catch (error) {
            console.error(error);
            toast.error(t('documentDeleteError'));
        } finally {
            setSaving(false);
        }
    };

    const handleAttachFiles = async (item, files) => {
        try {
            const response = await DocumentService.AttachOtherDocumentFiles(item.id, files, token);
            if (!response.ok) {
                toast.error(`${t('documentUploadError')}: ${await response.text()}`);
                return false;
            }
            toast.success(t('documentUploadedSuccess'));
            await loadDocuments();
            return true;
        } catch (error) {
            console.error(error);
            toast.error(t('documentUploadError'));
            return false;
        }
    };

    const handleDeleteFile = async (item) => {
        try {
            const response = await DocumentService.DeleteOtherDocumentFile(item.id, token);
            if (!response.ok) {
                toast.error(`${t('ErrorDeletingDocumentUrl')}: ${await response.text()}`);
                return false;
            }
            toast.success(t('DocumentUrlDeletedSuccessfully'));
            await loadDocuments();
            return true;
        } catch (error) {
            console.error(error);
            toast.error(t('ErrorDeletingDocumentUrl'));
            return false;
        }
    };

    const handleDeleteClick = (itemId) => {
        const item = data.find((row) => row.id === itemId);
        if (item) setDeleteTarget(item);
    };

    // ─── Upload ─────────────────────────────────
    const closeCreate = () => {
        setCreateOpen(false);
        setFileTransported(null);
        setMultiTransferItems(null);
        setPendingDeletes([]);
    };

    const buildFormData = (item, files) => {
        const formData = new FormData();
        formData.append('CaseId', client.caseId);
        formData.append('AccountantId', user.accountantId);
        formData.append('Title', item.title);
        formData.append('DocumentType', item.documentType);
        formData.append('DocumentDate', calendarDateToIso(item.documentDate));
        formData.append('DocumentFileType', type);
        files.forEach((file) => formData.append('documents', file, file.name));
        return formData;
    };

    const deleteSource = async ({ id, fromType }) => {
        try {
            const response = await DocumentService.DeleteDocument(fromType, id, token);
            if (response.ok) return true;
            toast.error(t('ErrorDeletingSourceDocument', { errorText: await response.text() }));
        } catch (error) {
            toast.error(t('ErrorDeletingSourceDocument', { errorText: error.message }));
        }
        return false;
    };

    const uploadMultiTransfer = async (item) => {
        let success = 0;
        let deleted = 0;
        const failedIds = [];
        for (const transferItem of multiTransferItems) {
            const files = toFiles(transferItem.files);
            const label = transferItem.dto?.id || transferItem.idToDelete || '?';
            if (!files.length) {
                failedIds.push(label);
                continue;
            }
            try {
                const response = await DocumentService.UploadDocument(type, buildFormData(item, files), token);
                if (!response.ok) {
                    failedIds.push(label);
                    continue;
                }
                success += 1;
                if (transferItem.idToDelete && transferItem.fileTypeToDelete
                    && await deleteSource({ id: transferItem.idToDelete, fromType: transferItem.fileTypeToDelete })) {
                    deleted += 1;
                }
            } catch {
                failedIds.push(label);
            }
        }
        toast.info(t('MultiTransferSummary', { selected: multiTransferItems.length, success, failed: failedIds.length, deleted }), { autoClose: 8000 });
        if (failedIds.length) toast.error(t('MultiTransferFailedDocuments', { ids: failedIds.join(', ') }), { autoClose: 10000 });
        return success > 0;
    };

    const handleSave = async (item, files) => {
        setSaving(true);
        try {
            if (item.isNewCategory) {
                const response = await EnumsService.AddOtherDocumentsType(
                    user.accountantId, item.documentType, token, item.hiddenFromClient, item.titleId || activeTitle?.id || null
                );
                if (!response.ok && response.status !== 409) {
                    toast.error(`${t('CategoryAddError')}: ${await response.text()}`);
                    return false;
                }
                if (response.ok) toast.success(t('CategoryAddedSuccessfully'));
            }

            let ok;
            if (Array.isArray(multiTransferItems) && multiTransferItems.length > 0) {
                ok = await uploadMultiTransfer(item);
            } else {
                const response = await DocumentService.UploadDocument(type, buildFormData(item, files), token);
                ok = response.ok;
                if (ok) {
                    for (const pending of pendingDeletes) {
                        if (await deleteSource(pending)) toast.success(t('SourceDocumentDeleted'));
                    }
                    toast.success(t('documentUploadedSuccess'));
                } else {
                    toast.error(`${t('documentUploadError')}: ${await response.text()}`);
                }
            }

            if (ok) {
                setFileTransported(null);
                setMultiTransferItems(null);
                setPendingDeletes([]);
                if (selectedType && normalize(selectedType) !== normalize(item.documentType)) setSelectedType('');
            }
            if (item.isNewCategory) await reloadAll();
            else await loadDocuments();
            await refreshClientCounts(client, token);
            return ok;
        } catch (error) {
            console.error(error);
            toast.error(t('documentUploadError'));
            return false;
        } finally {
            setSaving(false);
        }
    };

    // ─── Downloads ──────────────────────────────
    const handleDownloadZip = async ({ documentTypes, startDate, endDate }) => {
        setSaving(true);
        try {
            const blob = await DocumentService.DownloadCustomDocuments(type, token, {
                CaseId: client.caseId,
                Date: startDate ? startDate.toISOString() : null,
                EndDate: endDate ? endDate.toISOString() : null,
                OtherDocumentsTypeIds: documentTypes,
            });
            downloadBlob(blob, 'documents.zip');
            toast.success(t('documentDownloadSuccess'));
            return true;
        } catch (error) {
            console.error('Error downloading documents:', error);
            toast.error(t('documentDownloadError', { errorText: error.message }));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadMergedDocuments = async () => {
        const ids = new Set(selectedRows.map((row) => (typeof row === 'object' ? row.id : row)));
        const urls = data.filter((row) => ids.has(row.id) && row.documentUrl).map((row) => row.documentUrl);
        if (!urls.length) {
            toast.info(t('NoDocumentsFound'));
            return;
        }
        setLoading(true);
        try {
            const merged = await PDFDocument.create();
            for (const url of urls) {
                try {
                    const source = await PDFDocument.load(await fetch(url).then((r) => r.arrayBuffer()));
                    const pages = await merged.copyPages(source, source.getPageIndices());
                    pages.forEach((page) => merged.addPage(page));
                } catch (error) {
                    console.error(`Error loading PDF from ${url}:`, error);
                }
            }
            if (merged.getPageCount() === 0) {
                toast.error(t('NoDocumentsFound'));
                return;
            }
            downloadBlob(new Blob([await merged.save()], { type: 'application/pdf' }), `merged_documents_${Date.now()}.pdf`);
            toast.success(t('DocumentsDownloaded'));
        } catch (error) {
            console.error('Error merging documents:', error);
            toast.error(t('MergeOrPrintFailed'));
        } finally {
            setLoading(false);
        }
    };

    // ─── Titles / sections ──────────────────────
    const handleConvertPermanentFile = async () => {
        setLoading(true);
        try {
            const response = await EnumsService.EnsurePermanentFileTitle(user.accountantId, true, token);
            if (response.ok) {
                const title = await response.json();
                toast.success(t('PermanentFileConvertedSuccessfully'));
                setSection({ titleId: title.id, isPermanent: false });
                await reloadAll();
            } else {
                toast.error(`${t('TitleAddError')}: ${await response.text()}`);
            }
        } catch (error) {
            console.error(error);
            toast.error(t('TitleAddError'));
        } finally {
            setLoading(false);
        }
    };

    const handleToggleTitleHidden = async () => {
        if (!activeTitle) return;
        const hidden = !activeTitle.hiddenFromClient;
        setLoading(true);
        try {
            const response = await EnumsService.SetOtherDocumentsTitlesHidden(user.accountantId, [activeTitle.id], hidden, token);
            if (response.ok) {
                setTitles((prev) => prev.map((title) => (title.id === activeTitle.id ? { ...title, hiddenFromClient: hidden } : title)));
                toast.success(t(hidden ? 'TitlesHiddenSuccessfully' : 'TitlesShownSuccessfully'));
            } else {
                toast.error(`${t('TitleUpdateError')}: ${await response.text()}`);
            }
        } catch (error) {
            console.error(error);
            toast.error(t('TitleUpdateError'));
        } finally {
            setLoading(false);
        }
    };

    const handleFilterDateChange = (setter, current) => (value, part) => {
        const picked = new Date(value);
        setter(part === 'year'
            ? monthOf(picked.getUTCFullYear(), current.getUTCMonth())
            : monthOf(current.getUTCFullYear(), picked.getUTCMonth()));
    };

    const canBulk = canChangeStatus;
    const buttonData = [
        {
            label: 'Downloads',
            isDropdown: true,
            iconColor: 'green',
            dropdownItems: [
                { label: 'DownloadDocuments', onClick: () => setDownloadOpen(true) },
                {
                    label: 'DownloadSelectedDocuments',
                    onClick: handleDownloadMergedDocuments,
                    tooltip: selectedRows.length > 0 ? `${t('DownloadSelectedDocuments')} (${selectedRows.length})` : t('DownloadSelectedDocuments'),
                    disabled: selectedRows.length === 0 || loading,
                    show: canBulk,
                },
            ],
        },
        {
            label: 'Actions',
            isDropdown: true,
            iconColor: 'green',
            dropdownItems: [
                { label: 'UploadDocuments', onClick: () => setCreateOpen(true), icon: PostAddIcon },
                { label: 'OpenOptions', onClick: () => setStatusOptionsOpen(true), disabled: selectedRows.length === 0, show: canBulk },
                { label: 'DocumentTypes', onClick: () => setTypesModal('list'), icon: ListAltIcon, show: isAccountantOrSubAccountant },
                { label: 'AddCategory', onClick: () => setTypesModal('add'), icon: CreateNewFolderIcon, show: isAccountantOrSubAccountant },
                { label: 'ManageCategories', onClick: () => setCategoriesOpen(true), icon: SettingsSuggestIcon, show: isAccountantOrSubAccountant },
                { label: 'AddTitle', onClick: () => setCategoriesOpen(true), icon: FolderSpecialIcon, show: isAccountantOrSubAccountant },
                { label: 'AssignToTitle', onClick: () => setCategoriesOpen(true), icon: DriveFileMoveIcon, show: isAccountantOrSubAccountant },
                {
                    label: 'AddPermanentFileAsCategory',
                    onClick: handleConvertPermanentFile,
                    icon: FolderSpecialIcon,
                    disabled: loading,
                    show: isAccountantOrSubAccountant && isPermanentSection,
                },
                {
                    label: activeTitle?.hiddenFromClient ? 'ShowTitleToClient' : 'HideTitleFromClient',
                    onClick: handleToggleTitleHidden,
                    icon: activeTitle?.hiddenFromClient ? VisibilityIcon : VisibilityOffIcon,
                    disabled: loading,
                    show: isAccountantOrSubAccountant && !!activeTitle,
                },
            ],
        },
    ];

    const canTransfer = !isClientWithoutPremium;

    return (
        <>
            <DraggableModal
                bigIframeModalOpen={bigIframeModalOpen}
                openedIframeUrl={openedIframeUrl}
                handleCloseIframeModal={() => { setBigIframeModalOpen(false); setOpenedIframeUrl(''); }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} dir={direction}>
                <NavigationButton t={t} direction={direction} selectedRows={selectedRows} fileInputRef={fileInputRef} data={buttonData} />
            </Box>

            <div className={`searchFilter-bar__container ${pageStyles.toolbarRow} ${direction}`}>
                <Box className={pageStyles.filterRow} dir={direction}>
                    <TextField
                        size="small"
                        dir={direction}
                        placeholder={t('searchDocuments')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={pageStyles.searchBar}
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className={pageStyles.searchIcon} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div className={pageStyles.typeField}>
                        <div className={pageStyles.typeFieldTitle}>{t('otherDocumnets type')}</div>
                        <XtramSelect
                            direction={direction}
                            options={typeFilterOptions}
                            value={typeFilterOptions.find((option) => normalize(option.value) === normalize(selectedType)) || typeFilterOptions[0]}
                            onChange={(option) => setSelectedType(option?.value || '')}
                            disableClearable
                        />
                    </div>
                    <label className={`${headerStyles.switchRow} ${headerStyles[direction] || ''}`} dir={direction}>
                        <span>{t('DateRange')}</span>
                        <input type="checkbox" checked={filterByDateRange} onChange={() => setFilterByDateRange((prev) => !prev)} />
                        <span className={headerStyles.switchTrack} aria-hidden="true" />
                    </label>
                    {filterByDateRange && (
                        <div className={headerStyles.dateSelectRange}>
                            <div className={headerStyles.dateSelect}>
                                <FilterMonthYearPicker
                                    year={filterStartDate}
                                    month={filterStartDate}
                                    onYearChange={(value) => handleFilterDateChange(setFilterStartDate, filterStartDate)(value, 'year')}
                                    onMonthChange={(value) => handleFilterDateChange(setFilterStartDate, filterStartDate)(value, 'month')}
                                    labelYear={t('StartYear')}
                                    labelMonth={t('StartMonth')}
                                    direction={direction}
                                />
                            </div>
                            <div className={headerStyles.rangeDivider} />
                            <div className={headerStyles.dateSelect}>
                                <FilterMonthYearPicker
                                    year={filterEndDate}
                                    month={filterEndDate}
                                    onYearChange={(value) => handleFilterDateChange(setFilterEndDate, filterEndDate)(value, 'year')}
                                    onMonthChange={(value) => handleFilterDateChange(setFilterEndDate, filterEndDate)(value, 'month')}
                                    labelYear={t('EndYear')}
                                    labelMonth={t('EndMonth')}
                                    direction={direction}
                                />
                            </div>
                        </div>
                    )}
                </Box>
                {canChangeStatus && (
                    <Box className={pageStyles.modeToggles}>
                        <Box className={`${pageStyles.modeToggle} ${onlyUnprocessed ? pageStyles.modeToggleChecked : ''}`}>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        size="small"
                                        checked={onlyUnprocessed}
                                        onChange={(e) => setOnlyUnprocessed(e.target.checked)}
                                        inputProps={{ 'aria-label': t('ShowUnprocessedDocuments') }}
                                    />
                                )}
                                label={t('ShowUnprocessedDocuments')}
                            />
                        </Box>
                    </Box>
                )}
                {activeTitle && (
                    <Box className={pageStyles.titleBadges} dir={direction}>
                        <span className={pageStyles.titleBadge}>
                            <FolderSpecialIcon sx={{ fontSize: 16 }} />
                            {activeTitle.name}
                        </span>
                        {activeTitle.hiddenFromClient && isAccountantOrSubAccountant && (
                            <span className={`${pageStyles.titleBadge} ${pageStyles.titleBadgeHidden}`}>
                                <VisibilityOffIcon sx={{ fontSize: 16 }} />
                                {t('HiddenFromClient')}
                            </span>
                        )}
                    </Box>
                )}
            </div>

            <OtherDocumentDataGrid
                ref={dataGridRef}
                t={t}
                data={filteredData}
                client={client}
                subAccountantOptions={subAccountantOptions}
                openPdfInNewTab={(url) => { setOpenedIframeUrl(url || NoDocumentsPdf); setBigIframeModalOpen(true); }}
                handleEdit={(row) => setEditingId(row.id)}
                handleDeleteClick={handleDeleteClick}
                handleSelectRows={(ids) => setSelectedRows(ids || [])}
                handleRightClickAction={handleRightClickAction}
                direction={direction}
                permissionsState={permissionsState}
                userTypeContext={userTypeContext}
                hideStatus={hideStatusBadge}
                titleNameByType={titleNameByType}
            />

            <OtherDocumentCreateModal
                open={createOpen}
                onClose={closeCreate}
                onSave={handleSave}
                t={t}
                direction={direction}
                typeOptions={types}
                titles={titles}
                defaultDocumentType={selectedType && sectionTypes.some((item) => item.name === selectedType) ? selectedType : ''}
                defaultTitleId={activeTitle?.id || null}
                fileTransported={fileTransported}
                transferCount={multiTransferItems?.length || 0}
                canCreateCategory={isAccountantOrSubAccountant}
                busy={saving}
            />

            <OtherDocumentDownloadModal
                open={downloadOpen}
                onClose={() => setDownloadOpen(false)}
                onDownload={handleDownloadZip}
                t={t}
                direction={direction}
                typeOptions={sectionTypes}
                defaultTypes={selectedType ? [selectedType] : []}
                defaultRange={{ enabled: filterByDateRange, start: filterStartDate, end: filterEndDate }}
                busy={saving}
            />

            <OtherDocumentEditModal
                open={!!editingItem}
                onClose={() => setEditingId(null)}
                onSave={handleUpdate}
                onDelete={setDeleteTarget}
                onAttachFiles={handleAttachFiles}
                onDeleteFile={handleDeleteFile}
                onPrev={() => filteredData[editingIndex - 1] && setEditingId(filteredData[editingIndex - 1].id)}
                onNext={() => filteredData[editingIndex + 1] && setEditingId(filteredData[editingIndex + 1].id)}
                t={t}
                direction={direction}
                item={editingItem}
                index={editingIndex}
                total={filteredData.length}
                typeOptions={types}
                subAccountantOptions={subAccountantOptions}
                sectionTitle={editingItem ? titleNameByType[editingItem.documentType] : ''}
                clientName={client?.name}
                isUnprocessed={!!editingItem && unprocessedIds.has(editingItem.id)}
                canChangeStatus={canChangeStatus}
                canEditAdminFields={isAccountantOrSubAccountant}
                canDelete={!isClientWithoutPremium}
                suspendKeys={!!deleteTarget}
                busy={saving}
                transferAction={editingItem && canTransfer ? (
                    <DocumentTransferDialog
                        editedItem={{ ...editingItem, docType: 'OtherDocuments' }}
                        token={token}
                        setLoading={setLoading}
                        RefreshPage={loadDocuments}
                        t={t}
                        client={client}
                    />
                ) : null}
            />

            {isAccountantOrSubAccountant && (
                <>
                    <OtherDocumentTypesModal
                        open={!!typesModal}
                        onClose={() => setTypesModal(null)}
                        onChanged={reloadAll}
                        t={t}
                        direction={direction}
                        accountantId={user?.accountantId}
                        token={token}
                        types={types}
                        titles={titles}
                        startWithAdd={typesModal === 'add'}
                        defaultTitleId={activeTitle?.id || null}
                    />
                    <OtherDocumentCategoriesModal
                        open={categoriesOpen}
                        onClose={() => setCategoriesOpen(false)}
                        onChanged={reloadAll}
                        t={t}
                        direction={direction}
                        accountantId={user?.accountantId}
                        token={token}
                        types={types}
                        titles={titles}
                    />
                </>
            )}

            <XtramModal
                open={statusOptionsOpen && canChangeStatus}
                onClose={() => setStatusOptionsOpen(false)}
                title={t('SelectOption')}
                direction={direction}
                busy={saving}
                footer={<XtramButton variant="secondary" onClick={() => setStatusOptionsOpen(false)} disabled={saving}>{t('close')}</XtramButton>}
            >
                <XtramNotice type="info">
                    {t('PleaseSelectOptionForSelectedRows')} ({selectedRows.length})
                </XtramNotice>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mt: 2 }}>
                    <XtramButton variant="secondary" onClick={() => applyBulkStatus(false)} disabled={saving}>{t('unprocessed')}</XtramButton>
                    <XtramButton onClick={() => applyBulkStatus(true)} disabled={saving}>{t('processed')}</XtramButton>
                </Box>
            </XtramModal>

            <XtramModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                title={t('OtherDocumentsDeleteDocument')}
                direction={direction}
                busy={saving}
                footer={(
                    <>
                        <XtramButton variant="secondary" onClick={() => setDeleteTarget(null)} disabled={saving}>{t('Cancel')}</XtramButton>
                        <XtramButton variant="danger" onClick={handleConfirmDelete} disabled={saving} busy={saving}>{t('Delete')}</XtramButton>
                    </>
                )}
            >
                {deleteTarget && (
                    <XtramNotice type="warning">
                        {t('OtherDocumentsDeleteDocumentConfirm', {
                            title: deleteTarget.title || '-',
                            number: deleteTarget.uploadNumber ?? '-',
                        })}
                    </XtramNotice>
                )}
            </XtramModal>

            <ToastContainer position={direction === 'rtl' ? 'top-left' : 'top-right'} rtl={direction === 'rtl'} />
            {loading && (
                <div className="loader-container">
                    <PuffLoader size={100} loading={loading} />
                </div>
            )}
        </>
    );
}

export default OtherDocument;
